import { useExportActions } from "../../../../hooks/actions/useExportActions";
import { useStorage } from "../../../../hooks/useStorage";
import { addCopyWatermark } from "../../../../lib/utils/pdf-watermark";
import {
	DispatchResult,
	dispatchPrint,
	isAutoPrintEnabled,
} from "../../../../lib/utils/print-dispatch";
import { hidePaymentSlip } from "../../../../lib/utils/region-utils";
import { AddressRecipient } from "../../../../pdf-templates/utils";
import { useCentralStore } from "../../../../store/Central";
import { showNotification } from "../../../../store/Central/selectors";
import { useRemindersStore } from "../../../../store/Reminders";
import { getReminderSlipPreflightError } from "../utils/payment-reminder-document";

export interface ReminderFileRef {
	reminderId: number;
	stage: number;
	templateType: string;
	docNumber: string;
	pathName: string;
}

export interface CreateReminderResult {
	reminderId: number | null;
	error: string | null;
}

export interface ReminderSlipCheck {
	invoiceId: number;
	docNumber: string;
	clientId: string | null;
	currency: string | null;
	openAmount: number;
	needsFeeSlip: boolean;
}

const getReminderFileName = (docNumber: string, stage: number) =>
	`Mahnung-${docNumber}-${stage}.pdf`;

// A job queued for an offline desktop or a failed dispatch hasn't printed anything yet.
const wasPrinted = (result: DispatchResult) =>
	result.success && !result.desktopOffline;

// Runs before any stage is booked: invoices whose letter would carry a broken QR slip are
// reported instead of created. Slips follow the invoice rule (none outside the CH region).
const preflightReminderSlips = async (
	checks: ReminderSlipCheck[]
): Promise<Map<number, string>> => {
	const { organization, bankAccountsLookup, clientsLookup, activatedRegions } =
		useCentralStore.getState();
	const problems = new Map<number, string>();
	if (!organization) return problems;
	const withSlips = checks.filter(
		(check) =>
			!hidePaymentSlip(
				check.clientId ? clientsLookup[check.clientId] ?? null : null,
				organization,
				activatedRegions
			)
	);
	if (withSlips.length === 0) return problems;
	const context = await useRemindersStore
		.getState()
		.fetchSlipContext(withSlips.map((check) => check.invoiceId));
	if (!context) {
		withSlips.forEach((check) =>
			problems.set(check.invoiceId, "Zahlschein konnte nicht geprüft werden.")
		);
		return problems;
	}
	const contextByInvoice = new Map(
		context.map((row) => [row.invoice_id, row])
	);
	for (const check of withSlips) {
		const row = contextByInvoice.get(check.invoiceId);
		const bankAccountId =
			row?.bank_account_id ?? organization.default_bank_account_id;
		const error = getReminderSlipPreflightError({
			docNumber: check.docNumber,
			openAmount: check.openAmount,
			needsFeeSlip: check.needsFeeSlip,
			currency: check.currency || "CHF",
			bankAccount: bankAccountId
				? bankAccountsLookup[bankAccountId] ?? null
				: null,
			recipient: (row?.recipient ?? null) as unknown as AddressRecipient | null,
		});
		if (error) problems.set(check.invoiceId, `Zahlschein: ${error}`);
	}
	return problems;
};

export const usePaymentReminderPdf = () => {
	const { exportPaymentReminder } = useExportActions();
	const { fetchFileFromPath, downloadBlob } = useStorage();

	const fetchReminderBlob = async (pathName: string) => {
		const { data: signedUrl } = await fetchFileFromPath(pathName);
		if (!signedUrl) {
			showNotification({
				message: "Die Mahnung konnte nicht geladen werden.",
				type: "error",
			});
			return null;
		}
		return await (await fetch(signedUrl)).blob();
	};

	const printReminder = async (
		file: ReminderFileRef,
		options: { asCopy: boolean }
	) => {
		const blob = await fetchReminderBlob(file.pathName);
		if (!blob) return;
		const fileName = getReminderFileName(file.docNumber, file.stage);
		const printResult = await dispatchPrint({
			documentType: file.templateType,
			documentData: options.asCopy ? await addCopyWatermark(blob) : blob,
			fileName: options.asCopy ? `Kopie_${fileName}` : fileName,
		});
		if (!options.asCopy && wasPrinted(printResult)) {
			await useRemindersStore
				.getState()
				.markDelivered(file.reminderId, "printed_at");
		}
	};

	const downloadReminder = async (file: ReminderFileRef) => {
		const blob = await fetchReminderBlob(file.pathName);
		if (!blob) return;
		await downloadBlob(
			blob,
			getReminderFileName(file.docNumber, file.stage)
		);
		await useRemindersStore
			.getState()
			.markDelivered(file.reminderId, "downloaded_at");
	};

	// Creating books the stage (fee invoice + files row) in one RPC; the PDF is rendered
	// into the pre-allocated path afterwards. A failed render takes the booking back so no
	// reminder exists without its letter.
	const createReminder = async (
		invoiceId: number,
		ignoreSchedule: boolean,
		stage: number
	): Promise<CreateReminderResult> => {
		const store = useRemindersStore.getState();
		const { data, error } = await store.createReminder(
			invoiceId,
			ignoreSchedule,
			stage
		);
		if (!data) return { reminderId: null, error };

		const result = await exportPaymentReminder(data, data.file_path);
		if (!result.success) {
			const reason = result.error ?? "unbekannter Fehler";
			const voided = await store.voidReminder(
				data.reminder.id,
				"PDF konnte nicht erstellt werden",
				false
			);
			return {
				reminderId: voided ? null : data.reminder.id,
				error: voided
					? `PDF konnte nicht erstellt werden (${reason}). Die Mahnung wurde zurückgenommen.`
					: `PDF konnte nicht erstellt werden (${reason}). Bitte die Mahnung im Verlauf zurücknehmen.`,
			};
		}

		const templateType = data.reminder.template_type;
		if (result.blob && isAutoPrintEnabled(templateType)) {
			const printResult = await dispatchPrint({
				documentType: templateType,
				documentData: result.blob,
				fileName: getReminderFileName(
					data.invoice.doc_number,
					data.reminder.stage
				),
			});
			if (wasPrinted(printResult)) {
				await useRemindersStore
					.getState()
					.markDelivered(data.reminder.id, "printed_at");
			}
		}
		return { reminderId: data.reminder.id, error: null };
	};

	return {
		preflightReminderSlips,
		createReminder,
		printReminder,
		downloadReminder,
	};
};
